import { useEffect } from 'react';
import { AxiosResponse } from 'axios';
import { useMutation } from '@tanstack/react-query';
import * as service from '../services';
import { useDebounce } from '../../../../../hooks';
import { useAnaliseCoberturaStore, coverageInitialState } from '../AnaliseCobertura.store';
import { Filter, UfContent } from '../types';

export const useCoverageAnalysisUf = () => {
  const { setCoverageAnalysisState, ufCoverageParams, uf } = useAnaliseCoberturaStore();

  const debounce = useDebounce(500);

  const ufCoverageSuccess = (data: AxiosResponse) => {
    setCoverageAnalysisState('ufCoverageData', data.data);
  };

  const mutation = useMutation({
    mutationFn: data => service.getCoverageAnalysisUf(data),
    onSuccess: data => ufCoverageSuccess(data),
  });

  const handleChangeUfCoveragePeriod = (value: number) => {
    const param = { ...ufCoverageParams, diasAnalise: value };
    setCoverageAnalysisState('ufCoverageParams', param);
    mutation.mutateAsync(param);
  };

  const handleChangeUfCoveragePagination = (value: number) => {
    const param = { ...ufCoverageParams, page: value - 1 };
    setCoverageAnalysisState('ufCoverageParams', param);
    mutation.mutateAsync(param);
  };

  // FILTER

  const setList = (data: UfContent[]) => {
    const state = useAnaliseCoberturaStore.getState();
    const list = data?.map(item => ({
      value: item.uf,
      label: item.uf,
    }));

    const prev = state.uf;
    const cacheList = prev.cache;
    const removeDuplicate = list.filter(i => !prev.value?.includes(i.value));
    const concatList = prev.value?.length ? cacheList.concat(removeDuplicate) : list;
    setCoverageAnalysisState('uf', { ...prev, list: concatList });
  };

  const handleChangeUfCoverageSearch = async (value: string) => {
    const param = { ...ufCoverageParams, filtro: [], query: value, page: 0, size: 50 };

    debounce(async () => {
      const resp = await service.getCoverageAnalysisUf(param);

      if (resp?.status === 200) {
        setList(resp?.data.content);
      }
    });
  };

  const addSecao = (item: Filter) => {
    const param = ufCoverageParams;
    const prev = uf;

    if (!prev.value.includes(item.value)) {
      const value = [...prev.value, item.value];
      const cache = [...(prev.cache || []), item];
      setCoverageAnalysisState('uf', { ...prev, value, cache });
      setCoverageAnalysisState('ufCoverageParams', { ...param, filtro: value });
    }
  };

  const removeSecao = (item: Filter) => {
    const param = ufCoverageParams;
    const prev = uf;

    const value = prev.value.filter(value => value !== item.value);
    const cache = prev.cache?.filter(cache => cache.value !== item.value);
    setCoverageAnalysisState('uf', { ...prev, value, cache });
    setCoverageAnalysisState('ufCoverageParams', { ...param, filtro: value });
  };

  const handleSelectUfCoverageFilter = (item: Filter) => {
    const prev = uf;
    if (prev.value.includes(item.value)) {
      removeSecao(item);
    } else {
      addSecao(item);
    }
  };

  const handleCleanUfCoverageFilter = () => {
    const initialState = coverageInitialState.ufCoverageParams;
    const param = ufCoverageParams;
    const prev = uf;
    setCoverageAnalysisState('uf', { ...prev, value: [], cache: [] });
    setCoverageAnalysisState('ufCoverageParams', { ...param, filtro: [] });
    mutation.mutateAsync({ ...initialState, diasAnalise: param.diasAnalise, filtro: [] });
  };

  useEffect(() => {
    mutation.mutateAsync(ufCoverageParams);
  }, []);

  return {
    UfCoverageLoading: mutation.isPending,
    handleChangeUfCoveragePeriod,
    handleChangeUfCoverageSearch,
    handleChangeUfCoveragePagination,
    handleSelectUfCoverageFilter,
    handleCleanUfCoverageFilter,
    handleRequestUfCoverageTableList: mutation.mutateAsync,
  };
};
